/**
 * Recuperação de senha.
 *
 * O e-mail aponta para `/redefinir-senha?token=…` no web, inclusive para quem
 * pediu pelo app: o link abre no navegador do celular, e uma única tela atende
 * os dois clientes.
 */

import { CheckCircle2, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button, Field, Input } from '@/components/ui';
import { ApiError, api } from '@/lib/api';

import { AuthShell } from './login';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email: email.trim() }, { skipAuth: true });
      setSent(true);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Não foi possível enviar. Tente de novo.',
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell
      title="Esqueci a senha"
      subtitle="Mandamos um link para você criar uma senha nova."
      footer={
        <Link to="/entrar" className="font-semibold text-brand hover:underline">
          Voltar para o login
        </Link>
      }
    >
      {sent ? (
        // A mesma mensagem exista ou não a conta — a API também não diz.
        <div className="card flex items-start gap-3 p-5">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-ok" aria-hidden />
          <div className="text-sm text-ink-2">
            <p className="font-semibold text-ink">Confira seu e-mail</p>
            <p className="mt-1">
              Se {email.trim()} estiver cadastrado, você vai receber o link em alguns minutos. Ele
              vale por 30 minutos. Olhe também o spam.
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label="E-mail da conta" htmlFor="email" error={error ?? undefined}>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-ink-3" />
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="voce@email.com"
                className="pl-9"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                invalid={Boolean(error)}
                required
              />
            </div>
          </Field>

          <Button type="submit" size="lg" full loading={loading} disabled={!email.trim()}>
            Enviar link
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && confirm !== password;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (mismatch) return;
    setError(null);
    setLoading(true);

    try {
      await api.post('/auth/reset-password', { token, password }, { skipAuth: true });
      setDone(true);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? (caught.fieldErrors.password ?? caught.message)
          : 'Não foi possível salvar. Tente de novo.',
      );
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <AuthShell title="Link incompleto" subtitle="Este endereço não traz o código de redefinição.">
        <p className="text-sm text-ink-2">Abra o link direto do e-mail, ou peça um novo.</p>
        <Link to="/esqueci-a-senha" className="mt-4 block">
          <Button full size="lg">
            Pedir novo link
          </Button>
        </Link>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Senha alterada" subtitle="Pronto, já dá para entrar com a senha nova.">
        <p className="mb-4 text-sm text-ink-2">
          Por segurança, saímos da sua conta em todos os aparelhos. Entre de novo no app e no
          navegador.
        </p>
        <Link to="/entrar">
          <Button full size="lg">
            Entrar
          </Button>
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Criar nova senha"
      subtitle="Mínimo de 8 caracteres, com letra e número."
      footer={
        <Link to="/esqueci-a-senha" className="font-semibold text-brand hover:underline">
          Link expirou? Pedir outro
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Nova senha" htmlFor="password" error={error ?? undefined}>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-ink-3" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className="pl-9 pr-10"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              invalid={Boolean(error)}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              className="absolute right-3 top-3.5 text-ink-3 hover:text-ink-2"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </Field>

        <Field
          label="Repita a senha"
          htmlFor="confirm"
          error={mismatch ? 'As senhas não são iguais' : undefined}
        >
          <Input
            id="confirm"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            invalid={mismatch}
            required
          />
        </Field>

        <Button
          type="submit"
          size="lg"
          full
          loading={loading}
          disabled={!password || !confirm || mismatch}
        >
          Salvar nova senha
        </Button>
      </form>
    </AuthShell>
  );
}
